import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiArrowRight, FiCheck, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  postalCode: "",
  country: "Hong Kong SAR",
  phone: "",
  shippingMethod: "standard",
  paymentMethod: "card",
  cardNumber: "",
  expiration: "",
  securityCode: "",
  cardName: "",
  termsAccepted: false,
};

const passesLuhnCheck = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length !== 16) return false;

  const sum = digits
    .split("")
    .reverse()
    .reduce((total, digit, index) => {
      let number = Number(digit);
      if (index % 2 === 1) {
        number *= 2;
        if (number > 9) number -= 9;
      }
      return total + number;
    }, 0);

  return sum % 10 === 0;
};

const isValidExpiration = (value) => {
  const match = value.match(/^(\d{2}) \/ (\d{2})$/);
  if (!match) return false;

  const month = Number(match[1]);
  const year = 2000 + Number(match[2]);
  if (month < 1 || month > 12) return false;

  const now = new Date();
  const endOfMonth = new Date(year, month, 0, 23, 59, 59);
  return endOfMonth >= now;
};

const CheckOut = () => {
  const { cartItems, clearCart } = useContext(CartContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const processingTimer = useRef(null);

  useEffect(
    () => () => {
      if (processingTimer.current) {
        window.clearTimeout(processingTimer.current);
      }
    },
    []
  );

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const subtotal = detailedItems.reduce(
    (total, item) => total + item.product.price * item.quantity,
    0
  );
  const discount = location.state?.promoCode === "WELCOME10" ? subtotal * 0.1 : 0;
  const standardShipping =
    subtotal - discount >= siteConfig.commerce.freeShippingThreshold
      ? 0
      : siteConfig.commerce.standardShipping;
  const shipping = form.shippingMethod === "express" ? 32 : standardShipping;
  const total = subtotal - discount + shipping;

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((current) => ({ ...current, [name]: "" }));
  };

  const formatCardNumber = (event) => {
    const value = event.target.value
      .replace(/\D/g, "")
      .slice(0, 16)
      .replace(/(.{4})/g, "$1 ")
      .trim();
    setForm((current) => ({ ...current, cardNumber: value }));
    setErrors((current) => ({ ...current, cardNumber: "" }));
  };

  const formatExpiration = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 4);
    const value = digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits;
    setForm((current) => ({ ...current, expiration: value }));
    setErrors((current) => ({ ...current, expiration: "" }));
  };

  const validateStep = (currentStep) => {
    const nextErrors = {};

    if (currentStep === 1) {
      if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Enter a valid email address.";
      ["firstName", "lastName", "address", "city", "postalCode", "country"].forEach(
        (field) => {
          if (!String(form[field]).trim()) nextErrors[field] = "This field is required.";
        }
      );
    }

    if (currentStep === 2 && !form.shippingMethod) {
      nextErrors.shippingMethod = "Choose a delivery method.";
    }

    if (currentStep === 3) {
      if (form.paymentMethod === "card") {
        if (!passesLuhnCheck(form.cardNumber))
          nextErrors.cardNumber = "Enter a valid 16-digit card number.";
        if (!isValidExpiration(form.expiration))
          nextErrors.expiration = "Enter a future date in MM / YY format.";
        if (!/^\d{3,4}$/.test(form.securityCode))
          nextErrors.securityCode = "Enter 3 or 4 digits.";
        if (!form.cardName.trim()) nextErrors.cardName = "Enter the name on the card.";
      }
      if (!form.termsAccepted) nextErrors.termsAccepted = "Please accept the terms.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const continueCheckout = () => {
    if (!validateStep(step)) return;
    setStep((current) => Math.min(3, current + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const placeOrder = (event) => {
    event.preventDefault();
    if (!validateStep(3)) return;
    setIsProcessing(true);

    const orderNumber = `RO-${Date.now().toString().slice(-7)}`;
    processingTimer.current = window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", { state: { orderNumber, email: form.email } });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 700);
  };

  if (!detailedItems.length) {
    return (
      <main className="checkout-empty">
        <p className="eyebrow">Checkout</p>
        <h1>Your bag is empty</h1>
        <p>Add a piece before continuing to checkout.</p>
        <Link className="solid-button" to="/shop">Return to shop</Link>
      </main>
    );
  }

  return (
    <main className="checkout-page">
      <header className="checkout-heading">
        <p className="eyebrow">Secure checkout</p>
        <h1>Complete your order</h1>
        <div className="checkout-steps" aria-label="Checkout progress">
          {["Information", "Shipping", "Payment"].map((label, index) => (
            <button
              key={label}
              type="button"
              className={step === index + 1 ? "is-current" : step > index + 1 ? "is-complete" : ""}
              onClick={() => {
                if (index + 1 < step) setStep(index + 1);
              }}
              disabled={index + 1 > step}
            >
              <span>{step > index + 1 ? <FiCheck aria-hidden="true" /> : index + 1}</span>
              {label}
            </button>
          ))}
        </div>
      </header>

      <div className="checkout-layout">
        <section className="checkout-form-column">
          {step === 1 && (
            <form className="checkout-form" onSubmit={(event) => { event.preventDefault(); continueCheckout(); }} noValidate>
              <div className="checkout-section-title">
                <h2>Contact</h2>
                <span>Order updates and delivery notices</span>
              </div>
              <label className="checkout-field checkout-field-full">
                <span>Email address *</span>
                <input name="email" type="email" value={form.email} onChange={updateField} autoComplete="email" />
                {errors.email && <small>{errors.email}</small>}
              </label>

              <div className="checkout-section-title delivery-title">
                <h2>Delivery address</h2>
              </div>
              <div className="checkout-fields-grid">
                <label className="checkout-field">
                  <span>First name *</span>
                  <input name="firstName" value={form.firstName} onChange={updateField} autoComplete="given-name" />
                  {errors.firstName && <small>{errors.firstName}</small>}
                </label>
                <label className="checkout-field">
                  <span>Last name *</span>
                  <input name="lastName" value={form.lastName} onChange={updateField} autoComplete="family-name" />
                  {errors.lastName && <small>{errors.lastName}</small>}
                </label>
                <label className="checkout-field checkout-field-full">
                  <span>Company (optional)</span>
                  <input name="company" value={form.company} onChange={updateField} autoComplete="organization" />
                </label>
                <label className="checkout-field checkout-field-full">
                  <span>Address *</span>
                  <input name="address" value={form.address} onChange={updateField} autoComplete="street-address" />
                  {errors.address && <small>{errors.address}</small>}
                </label>
                <label className="checkout-field checkout-field-full">
                  <span>Apartment, suite, etc. (optional)</span>
                  <input name="apartment" value={form.apartment} onChange={updateField} />
                </label>
                <label className="checkout-field">
                  <span>City *</span>
                  <input name="city" value={form.city} onChange={updateField} autoComplete="address-level2" />
                  {errors.city && <small>{errors.city}</small>}
                </label>
                <label className="checkout-field">
                  <span>Postal code *</span>
                  <input name="postalCode" value={form.postalCode} onChange={updateField} autoComplete="postal-code" />
                  {errors.postalCode && <small>{errors.postalCode}</small>}
                </label>
                <label className="checkout-field checkout-field-full">
                  <span>Country / region *</span>
                  <select name="country" value={form.country} onChange={updateField} autoComplete="country-name">
                    {[
                      "Belgium",
                      "France",
                      "Germany",
                      "Netherlands",
                      "United Kingdom",
                      "United States",
                      "Hong Kong SAR",
                      "China",
                      "Japan",
                      "Australia",
                    ].map((country) => <option key={country}>{country}</option>)}
                  </select>
                  {errors.country && <small>{errors.country}</small>}
                </label>
                <label className="checkout-field checkout-field-full">
                  <span>Phone (optional)</span>
                  <input name="phone" type="tel" value={form.phone} onChange={updateField} autoComplete="tel" />
                </label>
              </div>
              <div className="checkout-navigation checkout-navigation-end">
                <button className="checkout-primary" type="submit">
                  Continue to shipping <FiArrowRight aria-hidden="true" />
                </button>
              </div>
            </form>
          )}

          {step === 2 && (
            <section className="checkout-form">
              <div className="checkout-section-title">
                <h2>Shipping method</h2>
                <span>Available services for your destination</span>
              </div>
              <div className="shipping-methods">
                <label className={form.shippingMethod === "standard" ? "is-selected" : ""}>
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="standard"
                    checked={form.shippingMethod === "standard"}
                    onChange={updateField}
                  />
                  <span>
                    <strong>Standard tracked delivery</strong>
                    <small>2–6 business days</small>
                  </span>
                  <b>{standardShipping ? formatPrice(standardShipping) : "Complimentary"}</b>
                </label>
                <label className={form.shippingMethod === "express" ? "is-selected" : ""}>
                  <input
                    type="radio"
                    name="shippingMethod"
                    value="express"
                    checked={form.shippingMethod === "express"}
                    onChange={updateField}
                  />
                  <span>
                    <strong>Express delivery</strong>
                    <small>1–3 business days</small>
                  </span>
                  <b>{formatPrice(32)}</b>
                </label>
              </div>
              {errors.shippingMethod && <p className="checkout-error">{errors.shippingMethod}</p>}
              <div className="delivery-address-preview">
                <p>Deliver to</p>
                <span>{form.firstName} {form.lastName}</span>
                <span>{form.address}{form.apartment ? `, ${form.apartment}` : ""}</span>
                <span>{form.postalCode} {form.city}, {form.country}</span>
              </div>
              <div className="checkout-navigation">
                <button type="button" className="checkout-back" onClick={() => setStep(1)}>
                  <FiArrowLeft aria-hidden="true" /> Back
                </button>
                <button type="button" className="checkout-primary" onClick={continueCheckout}>
                  Continue to payment <FiArrowRight aria-hidden="true" />
                </button>
              </div>
            </section>
          )}

          {step === 3 && (
            <form className="checkout-form" onSubmit={placeOrder} noValidate>
              <div className="checkout-section-title">
                <h2>Payment</h2>
                <span>All transactions are encrypted</span>
              </div>

              <div className="payment-methods">
                <label className={form.paymentMethod === "card" ? "is-selected" : ""}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="card"
                    checked={form.paymentMethod === "card"}
                    onChange={updateField}
                  />
                  <span>Credit card</span>
                  <span className="payment-marks"><b>VISA</b><b>MC</b><b>AMEX</b></span>
                </label>
                <label className={form.paymentMethod === "paypal" ? "is-selected" : ""}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="paypal"
                    checked={form.paymentMethod === "paypal"}
                    onChange={updateField}
                  />
                  <span>PayPal</span>
                </label>
              </div>

              {form.paymentMethod === "card" && (
                <div className="card-fields checkout-fields-grid">
                  <label className="checkout-field checkout-field-full">
                    <span>Card number *</span>
                    <input
                      name="cardNumber"
                      inputMode="numeric"
                      value={form.cardNumber}
                      onChange={formatCardNumber}
                      placeholder="1234 5678 9012 3456"
                      autoComplete="cc-number"
                    />
                    {errors.cardNumber && <small>{errors.cardNumber}</small>}
                  </label>
                  <label className="checkout-field">
                    <span>Expiration *</span>
                    <input
                      name="expiration"
                      inputMode="numeric"
                      value={form.expiration}
                      onChange={formatExpiration}
                      placeholder="MM / YY"
                      autoComplete="cc-exp"
                    />
                    {errors.expiration && <small>{errors.expiration}</small>}
                  </label>
                  <label className="checkout-field">
                    <span>Security code *</span>
                    <input
                      name="securityCode"
                      inputMode="numeric"
                      value={form.securityCode}
                      onChange={updateField}
                      maxLength="4"
                      placeholder="CVV"
                      autoComplete="cc-csc"
                    />
                    {errors.securityCode && <small>{errors.securityCode}</small>}
                  </label>
                  <label className="checkout-field checkout-field-full">
                    <span>Name on card *</span>
                    <input
                      name="cardName"
                      value={form.cardName}
                      onChange={updateField}
                      autoComplete="cc-name"
                    />
                    {errors.cardName && <small>{errors.cardName}</small>}
                  </label>
                </div>
              )}

              {form.paymentMethod === "paypal" && (
                <p className="payment-placeholder">
                  In this frontend demonstration, the PayPal handoff is represented
                  by the final order confirmation.
                </p>
              )}

              <label className="terms-check">
                <input
                  type="checkbox"
                  name="termsAccepted"
                  checked={form.termsAccepted}
                  onChange={updateField}
                />
                <span>
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" rel="noreferrer">
                    terms and conditions
                  </Link>{" "}
                  and acknowledge the{" "}
                  <Link to="/privacy" target="_blank" rel="noreferrer">
                    privacy policy
                  </Link>
                  .
                  {errors.termsAccepted && <small>{errors.termsAccepted}</small>}
                </span>
              </label>

              <p className="checkout-demo-note">
                <FiLock aria-hidden="true" /> Demo checkout: payment details stay in
                this browser and are not transmitted.
              </p>

              <div className="checkout-navigation">
                <button type="button" className="checkout-back" onClick={() => setStep(2)}>
                  <FiArrowLeft aria-hidden="true" /> Back
                </button>
                <button type="submit" className="checkout-primary" disabled={isProcessing}>
                  {isProcessing ? "Processing…" : `Pay ${formatPrice(total)}`}
                </button>
              </div>
            </form>
          )}
        </section>

        <aside className="checkout-summary">
          <h2>Order summary</h2>
          <div className="checkout-products">
            {detailedItems.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div className="checkout-product-image">
                  <img src={item.product.thumbnail} alt={item.product.name} />
                  <span>{item.quantity}</span>
                </div>
                <div>
                  <h3>{item.product.name}</h3>
                  <p>{item.color} / {item.size}</p>
                </div>
                <strong>{formatPrice(item.product.price * item.quantity)}</strong>
              </article>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            {discount > 0 && (
              <div><dt>WELCOME10</dt><dd>−{formatPrice(discount)}</dd></div>
            )}
            <div><dt>Delivery</dt><dd>{shipping ? formatPrice(shipping) : "Complimentary"}</dd></div>
            <div className="checkout-total">
              <dt>Total <small>VAT included</small></dt>
              <dd>{formatPrice(total)}</dd>
            </div>
          </dl>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
